// src/components/About/ReviewSection.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Rating,
  Snackbar,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import axios from "axios";
import { sanity } from "../../lib/sanity";
import { featuredReviewsQuery } from "../../groq/featuredReviews";

const ReviewSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    location: "",
    rating: 5,
    review: "",
  });
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [errors, setErrors] = useState({});

  // Calculate average rating from visible reviews
  const calculateAverageRating = (reviews) => {
    if (!reviews || reviews.length === 0) return 0;
    const validRatings = reviews
      .map(r => r.rating)
      .filter(rating => rating && rating >= 1 && rating <= 5);
    if (validRatings.length === 0) return 0;
    const sum = validRatings.reduce((acc, rating) => acc + rating, 0);
    return Math.round((sum / validRatings.length) * 10) / 10; // Round to 1 decimal place
  };

  // fetch + realtime listen
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const data = await sanity.fetch(featuredReviewsQuery);
        // Sort: rating DESC, then createdAt DESC (as tie-breaker)
        const sortedData = (data || []).sort((a, b) => {
          if (b.rating !== a.rating) {
            return (b.rating || 0) - (a.rating || 0);
          }
          // Tie-breaker: newest first
          const dateA = a._createdAt ? new Date(a._createdAt) : new Date(0);
          const dateB = b._createdAt ? new Date(b._createdAt) : new Date(0);
          return dateB - dateA;
        });
        setReviews(sortedData);
      } catch (err) {
        console.error("Sanity fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    // initial load
    fetchReviews();

    // 🔴 realtime: refetch whenever any review doc changes (approve/feature/reply/publish)
    const sub = sanity
      .listen(`*[_type == "review"]`, {}, { includeResult: false, visibility: "query" })
      .subscribe({
        next: () => fetchReviews(),
        error: (err) => console.error("Sanity listen error:", err),
      });

    return () => sub.unsubscribe();
  }, []);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) return;

    const newErrors = {};

    // Validate required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.review.trim()) {
      newErrors.review = "Review text is required";
    }
    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = "Please select a rating";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setToast({ open: true, message: "Please fill in all required fields correctly.", severity: "warning" });
      return;
    }

    setSubmitting(true);

    try {
      // Map form fields to Sanity schema fields
      const reviewData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone ? formData.phone.trim() : "",
        location: formData.location ? formData.location.trim() : "",
        rating: formData.rating,
        reviewText: formData.review.trim(),
      };

      console.log("Submitting review data:", JSON.stringify(reviewData, null, 2));
      const response = await axios.post("/api/reviews", reviewData);
      console.log("Review submitted successfully:", response.data);

      // Reset form to initial state
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        location: "",
        rating: 5,
        review: "",
      });
      setErrors({});

      // Show success message
      setToast({
        open: true,
        message: "Thanks! Your review was submitted and is awaiting approval.",
        severity: "success",
      });

      // Note: No immediate refetch - review won't appear until approved + featured in Sanity Studio
    } catch (err) {
      console.error("Error submitting review:", err);
      const errorMessage = err.response?.data?.error || err.message || "Error submitting review. Please try again.";
      setToast({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 8 }}>
      <Typography variant="h4" textAlign="center" gutterBottom fontWeight="bold">
        Customer Reviews
      </Typography>
      <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
        See what our customers say about us — and share your experience
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {/* Form (left) */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Write a Review
            </Typography>
            <form onSubmit={handleSubmit} noValidate>
              {/* First Name and Last Name - side by side on desktop, stacked on mobile */}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    margin="normal"
                    value={formData.firstName}
                    onChange={(e) => {
                      setFormData({ ...formData, firstName: e.target.value });
                      if (errors.firstName) setErrors({ ...errors, firstName: "" });
                    }}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    margin="normal"
                    value={formData.lastName}
                    onChange={(e) => {
                      setFormData({ ...formData, lastName: e.target.value });
                      if (errors.lastName) setErrors({ ...errors, lastName: "" });
                    }}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                margin="normal"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (errors.email) setErrors({ ...errors, email: "" });
                }}
                error={!!errors.email}
                helperText={errors.email}
              />

              <TextField
                fullWidth
                label="Phone (optional)"
                margin="normal"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />

              <TextField
                fullWidth
                label="City / Location (optional)"
                margin="normal"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />

              <Box sx={{ my: 2 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Rating *
                </Typography>
                <Rating
                  value={formData.rating}
                  onChange={(_, v) => {
                    setFormData({ ...formData, rating: v || 0 });
                    if (errors.rating) setErrors({ ...errors, rating: "" });
                  }}
                />
                {errors.rating && (
                  <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                    {errors.rating}
                  </Typography>
                )}
              </Box>

              <TextField
                fullWidth
                required
                label="Your Review"
                multiline
                rows={4}
                margin="normal"
                value={formData.review}
                onChange={(e) => {
                  setFormData({ ...formData, review: e.target.value });
                  if (errors.review) setErrors({ ...errors, review: "" });
                }}
                error={!!errors.review}
                helperText={errors.review}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{ mt: 2, backgroundColor: "#5C4033", "&:hover": { backgroundColor: "#3E2A1F" } }}
              >
                {submitting ? "Submitting..." : "Submit Review"}
              </Button>
            </form>
          </Paper>
        </Grid>

        {/* Reviews (right) */}
        <Grid item xs={12} md={8}>
          {loading ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>Loading reviews…</Paper>
          ) : reviews.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>No reviews to display right now.</Paper>
          ) : (
            <Box 
              sx={{ 
                maxWidth: "950px",
                mx: "auto",
                display: "flex",
                flexDirection: "column"
              }}
            >
              {/* Average Rating Section */}
              <Box 
                sx={{ 
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap"
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ mr: 1 }}>
                  Average Rating
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {calculateAverageRating(reviews).toFixed(1)} / 5
                  </Typography>
                  <Rating 
                    value={calculateAverageRating(reviews)} 
                    readOnly 
                    precision={0.1}
                    sx={{ ml: 0.5 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                </Typography>
              </Box>

              {/* Reviews List */}
              <Box 
                sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  gap: 2
                }}
              >
              {reviews.map((r) => (
                <Card 
                  key={r._id}
                  sx={{ 
                    width: "100%",
                    borderRadius: 2, 
                    boxShadow: 4, 
                    backgroundColor: "#FFFFFF",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    display: "flex",
                    flexDirection: "column"
                  }}
                >
                    <CardContent 
                      sx={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        p: 3
                      }}
                    >
                      {/* Name Section */}
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }} noWrap>
                        {[r.firstName, r.lastName].filter(Boolean).join(" ") || "Anonymous"}
                      </Typography>
                      
                      {/* Location Section */}
                      {r.location && (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }} display="block">
                          {r.location}
                        </Typography>
                      )}
                      
                      {/* Rating Section */}
                      <Box sx={{ mb: 1.5 }}>
                        <Rating value={r.rating ?? 0} readOnly size="small" />
                      </Box>
                      
                      {/* Review Text Section - Clamped to 3 lines */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mb: r.ownerReply ? 1.5 : 0,
                          lineHeight: 1.6,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical"
                        }}
                      >
                        {r.reviewText}
                      </Typography>
                      
                      {/* Owner Reply Section - Only rendered when present, clamped to 2 lines */}
                      {r.ownerReply && (
                        <Typography
                          variant="body2"
                          sx={{ 
                            fontStyle: "italic", 
                            color: "text.secondary", 
                            borderLeft: "3px solid #5C4033", 
                            pl: 1.5,
                            pt: 1,
                            lineHeight: 1.5,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical"
                          }}
                        >
                          <strong>SD Solutions Reply:</strong> {r.ownerReply}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
              ))}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewSection;
